/**
 * useFormulaClipboard — copy/paste hook for formula groups and configs.
 *
 * Writes a JSON payload to the system clipboard and persists a local history
 * so copies stay available across drawer sessions and campaigns.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useState } from 'react';
import type { FormulaConfig, FormulaGroup } from '@trafficpulse/shared';

import { useToast } from '@/providers/ToastProvider';

import {
  buildFormulaClipboardPayload,
  buildGroupClipboardPayload,
  parseClipboardJson,
  payloadToJson,
} from './formula-clipboard.serialize';
import {
  clearHistory as clearHistoryStore,
  pushHistory,
  readHistory,
} from './formula-clipboard.history';
import type {
  FormulaClipboardHistoryEntry,
  FormulaClipboardPayload,
} from './formula-clipboard.types';

async function writeSystemClipboard(text: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

async function readSystemClipboard(): Promise<string | null> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) return null;
  try {
    return await navigator.clipboard.readText();
  } catch {
    return null;
  }
}

export interface UseFormulaClipboardResult {
  history: FormulaClipboardHistoryEntry[];
  copyGroup: (group: FormulaGroup) => Promise<void>;
  copyFormula: (formula: FormulaConfig) => Promise<void>;
  pasteFromSystem: () => Promise<FormulaClipboardPayload | null>;
  clearHistory: () => void;
  refreshHistory: () => void;
}

export function useFormulaClipboard(): UseFormulaClipboardResult {
  const toast = useToast();
  const [history, setHistory] = useState<FormulaClipboardHistoryEntry[]>(() => readHistory());

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'traffic-pulse:formula-clipboard:history') setHistory(readHistory());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const announceCopy = useCallback(
    async (payload: FormulaClipboardPayload) => {
      const wrote = await writeSystemClipboard(payloadToJson(payload));
      setHistory(pushHistory(payload));
      if (wrote) toast.success('Copied formula', payload.label);
      else toast.warning('Saved to local history', 'Clipboard access denied; entry available in Paste menu.');
    },
    [toast],
  );

  const copyGroup = useCallback(
    (group: FormulaGroup) => announceCopy(buildGroupClipboardPayload(group)),
    [announceCopy],
  );

  const copyFormula = useCallback(
    (formula: FormulaConfig) => announceCopy(buildFormulaClipboardPayload(formula)),
    [announceCopy],
  );

  const pasteFromSystem = useCallback(async (): Promise<FormulaClipboardPayload | null> => {
    const text = await readSystemClipboard();
    if (!text) {
      toast.error('Clipboard empty', 'Copy a formula first or use the history menu.');
      return null;
    }
    const parsed = parseClipboardJson(text);
    if (!parsed) {
      toast.error('Invalid clipboard payload', 'Expected a TrafficPulse formula JSON.');
      return null;
    }
    return parsed;
  }, [toast]);

  const clearHistory = useCallback(() => {
    clearHistoryStore();
    setHistory([]);
  }, []);

  const refreshHistory = useCallback(() => setHistory(readHistory()), []);

  return { history, copyGroup, copyFormula, pasteFromSystem, clearHistory, refreshHistory };
}
